let pendingRenderLoads = 0;
const pendingRenderLoadListeners = new Set<() => void>();

function notifyPendingRenderLoads(): void {
  for (const listener of pendingRenderLoadListeners) {
    listener();
  }
}

/**
 * Counts the module loads that a render waits for. A lazy component commits
 * an empty fallback while its module loads, so an automatic print waits until
 * this count is zero.
 */
export const pendingRenderLoadStore = {
  getSnapshot: (): number => pendingRenderLoads,
  subscribe: (listener: () => void): (() => void) => {
    pendingRenderLoadListeners.add(listener);

    return () => pendingRenderLoadListeners.delete(listener);
  }
};

function beginRenderLoad(): () => void {
  pendingRenderLoads += 1;
  // A render starts the load, and a store must not notify a subscriber while
  // React renders a different component.
  queueMicrotask(notifyPendingRenderLoads);

  return () => {
    pendingRenderLoads -= 1;
    notifyPendingRenderLoads();
  };
}

/**
 * A module that loads once. `loaded()` returns it synchronously after the
 * load, or after `seed()` hands over a module that a static import already
 * evaluated. A failed `preload()` is forgotten, so the next `preload()` tries
 * again.
 *
 * `renderLoad()` is the load that a render waits for. It runs once: after it
 * fails, `renderLoad()` returns null and `renderFailure()` keeps the error, so
 * a render reports the failure instead of suspending on a new load each time
 * React retries it.
 */
export type ModuleLoader<TModule> = {
  loaded: () => TModule | null;
  preload: () => Promise<TModule>;
  renderLoad: () => Promise<void> | null;
  renderFailure: () => { error: unknown } | null;
  seed: (module: TModule) => void;
};

export function moduleLoader<TModule>(load: () => Promise<TModule>): ModuleLoader<TModule> {
  let module: TModule | null = null;
  let loading: Promise<TModule> | null = null;
  let renderLoading: Promise<void> | null = null;
  let renderFailure: { error: unknown } | null = null;

  const preload = () => {
    if (module !== null) {
      return Promise.resolve(module);
    }

    loading ??= load().then(
      (loadedModule) => {
        module = loadedModule;

        return loadedModule;
      },
      (error: unknown) => {
        loading = null;

        throw error;
      }
    );

    return loading;
  };

  return {
    loaded: () => module,
    preload,
    renderLoad: () => {
      if (module !== null || renderFailure !== null) {
        return null;
      }

      if (renderLoading === null) {
        const endRenderLoad = beginRenderLoad();

        renderLoading = preload().then(
          () => {
            endRenderLoad();
          },
          (error: unknown) => {
            renderFailure = { error };
            endRenderLoad();
          }
        );
      }

      return renderLoading;
    },
    renderFailure: () => renderFailure,
    seed: (seeded) => {
      module = seeded;
    }
  };
}

/**
 * Reads a loader's module during a render, or throws for Suspense. The read
 * waits for the loader's render load, so a load that fails reaches the error
 * boundary instead of starting a new load on each retry render. React does not
 * retry a `use()` of a promise that the render created, so the read throws the
 * load.
 */
export function suspendingModuleRead<TModule>(loader: ModuleLoader<TModule>): () => TModule {
  return () => {
    const module = loader.loaded();

    if (module !== null) {
      return module;
    }

    const failure = loader.renderFailure();

    if (failure !== null) {
      throw failure.error;
    }

    throw loader.renderLoad();
  };
}

/** The part of a `ModuleLoader` that a load of source runtime uses. */
export type RuntimeLoader = Pick<ModuleLoader<unknown>, 'loaded' | 'preload' | 'renderLoad'>;

/** Starts a load of a loader that a source names, or returns null when it needs none. */
export type RuntimeLoad = (loader: RuntimeLoader) => Promise<unknown> | null;

/**
 * A load that its caller awaits. A failed load rejects, and the next call
 * tries again.
 */
export const explicitRuntimeLoad: RuntimeLoad = (loader) =>
  loader.loaded() === null ? loader.preload() : null;

/**
 * A load that a render suspends on. After it fails, it returns null, so the
 * render goes on and the member reports the failure to its error boundary.
 */
export const renderRuntimeLoad: RuntimeLoad = (loader) => loader.renderLoad();

/**
 * The lazy members of the Page SDK, keyed by the member name that the page
 * build records (`Components.DataTable`, `hooks.useDataTable`, `Dnd`). Each
 * member has its one loader here: the registry and the lazy hooks render
 * through it, `rowRuntime.ts` seeds the components that authoring source
 * renders in its first frame, a row-based page loads the seeded members its
 * source names, and a module page preloads the members its build recorded.
 *
 * scripts/lib/vite-sdk-member-chunks.mjs reads the `import()` path of each
 * entry from this file, so every entry stays a literal
 * `name: moduleLoader(() => import('./path'))`.
 */
export const sdkMemberLoaders = {
  'Components.Accordion': moduleLoader(() => import('./components/Accordion')),
  'Components.AdminNavButtons': moduleLoader(() => import('./components/AdminNavButtons')),
  'Components.AiAgentButton': moduleLoader(() => import('./components/AiAgentButton')),
  'Components.Alert': moduleLoader(() => import('./components/Alert')),
  'Components.AnalyticsEmbed': moduleLoader(() => import('./components/AnalyticsEmbed')),
  'Components.Avatar': moduleLoader(() => import('./components/Avatar')),
  'Components.Badge': moduleLoader(() => import('./components/ui/Badge')),
  'Components.Breadcrumb': moduleLoader(() => import('./components/Navigation')),
  'Components.ButtonLink': moduleLoader(() => import('./components/Navigation')),
  'Components.Calendar': moduleLoader(() => import('./components/calendar/CalendarView')),
  'Components.CalendarConnections': moduleLoader(() => import('./components/CalendarConnections')),
  'Components.CalendarNextButton': moduleLoader(
    () => import('./components/calendar/CalendarHeaderComponents')
  ),
  'Components.CalendarPrevButton': moduleLoader(
    () => import('./components/calendar/CalendarHeaderComponents')
  ),
  'Components.CalendarSelector': moduleLoader(
    () => import('./components/calendar/CalendarSelector')
  ),
  'Components.CalendarTitle': moduleLoader(
    () => import('./components/calendar/CalendarHeaderComponents')
  ),
  'Components.CalendarTodayButton': moduleLoader(
    () => import('./components/calendar/CalendarHeaderComponents')
  ),
  'Components.CalendarViewSwitcher': moduleLoader(
    () => import('./components/calendar/CalendarHeaderComponents')
  ),
  'Components.Card': moduleLoader(() => import('./components/Card')),
  'Components.Collapsible': moduleLoader(() => import('./components/Collapsible')),
  'Components.ConfirmDialog': moduleLoader(() => import('./components/ConfirmDialog')),
  'Components.CreateResourceRecordForm': moduleLoader(
    () => import('./components/CreateResourceRecordForm')
  ),
  'Components.DataTable': moduleLoader(() => import('./components/DataTable')),
  'Components.DataTableAddFilter': moduleLoader(() => import('./components/DataTableFilterChips')),
  'Components.DataTableBulkActions': moduleLoader(
    () => import('./components/ui/data-table/DataTableBulkActions')
  ),
  'Components.DataTableColumnToggle': moduleLoader(
    () => import('./components/DataTableColumnToggle')
  ),
  'Components.DataTableContent': moduleLoader(
    () => import('./components/ui/data-table/DataTableContent')
  ),
  'Components.DataTableFilterChips': moduleLoader(
    () => import('./components/DataTableFilterChips')
  ),
  'Components.DataTableFilters': moduleLoader(() => import('./components/DataTableFilters')),
  'Components.DataTablePagination': moduleLoader(
    () => import('./components/ui/data-table/DataTablePagination')
  ),
  'Components.DataTableRowActions': moduleLoader(
    () => import('./components/ui/data-table/DataTableRowActions')
  ),
  'Components.DataTableSearch': moduleLoader(
    () => import('./components/ui/data-table/DataTableSearch')
  ),
  'Components.DataTableToolbar': moduleLoader(
    () => import('./components/ui/data-table/DataTableToolbar')
  ),
  'Components.DeleteResourceRecord': moduleLoader(
    () => import('./components/DeleteResourceRecord')
  ),
  'Components.DropdownMenu': moduleLoader(() => import('./components/Navigation')),
  'Components.EmailComposer': moduleLoader(() => import('./components/EmailComposer')),
  'Components.EmailMessages': moduleLoader(() => import('./components/EmailMessages')),
  'Components.EmptyState': moduleLoader(() => import('./components/EmptyState')),
  'Components.ErrorBoundary': moduleLoader(() => import('./components/ErrorBoundary')),
  'Components.ForgotPasswordForm': moduleLoader(() => import('./components/ForgotPasswordForm')),
  'Components.FormCheckbox': moduleLoader(() => import('./components/forms/Checkbox')),
  'Components.FormCheckboxGroupInput': moduleLoader(
    () => import('./components/forms/CheckboxGroupInput')
  ),
  'Components.FormCodeInput': moduleLoader(() => import('./components/forms/CodeInput')),
  'Components.FormColorInput': moduleLoader(() => import('./components/ui/form/FormColorInput')),
  'Components.FormComboboxInput': moduleLoader(() => import('./components/forms/ComboboxInput')),
  'Components.FormContentInput': moduleLoader(() => import('./components/forms/ContentInput')),
  'Components.FormDateInput': moduleLoader(() => import('./components/ui/form/FormDateInput')),
  'Components.FormDatetimeInput': moduleLoader(
    () => import('./components/ui/form/FormDatetimeInput')
  ),
  'Components.FormFieldDisplay': moduleLoader(() => import('./components/forms/FieldDisplay')),
  'Components.FormFieldInput': moduleLoader(() => import('./components/forms/FieldInput')),
  'Components.FormFileUpload': moduleLoader(() => import('./components/ui/form/FormFileInput')),
  'Components.FormLocationInput': moduleLoader(
    () => import('./components/ui/form/FormLocationInput')
  ),
  'Components.FormMarkdownInput': moduleLoader(() => import('./components/forms/MarkdownInput')),
  'Components.FormMoneyInput': moduleLoader(() => import('./components/ui/form/FormMoneyInput')),
  'Components.FormMultiComboboxInput': moduleLoader(
    () => import('./components/forms/MultiComboboxInput')
  ),
  'Components.FormNumberInput': moduleLoader(() => import('./components/forms/NumberInput')),
  'Components.FormPasswordInput': moduleLoader(
    () => import('./components/ui/form/FormPasswordInput')
  ),
  'Components.FormPhoneInput': moduleLoader(() => import('./components/forms/PhoneInput')),
  'Components.FormRadio': moduleLoader(() => import('./components/forms/Radio')),
  'Components.FormSelectInput': moduleLoader(() => import('./components/forms/SelectInput')),
  'Components.FormSlider': moduleLoader(() => import('./components/forms/Slider')),
  'Components.FormSwitch': moduleLoader(() => import('./components/forms/Switch')),
  'Components.FormTextArea': moduleLoader(() => import('./components/ui/form/FormTextarea')),
  'Components.FormTextInput': moduleLoader(() => import('./components/ui/form/FormTextInput')),
  'Components.FormTimeInput': moduleLoader(() => import('./components/ui/form/FormTimeInput')),
  'Components.FormTimezoneInput': moduleLoader(() => import('./components/forms/TimezoneInput')),
  'Components.HasAnyPermission': moduleLoader(() => import('./components/HasPermission')),
  'Components.HasPermission': moduleLoader(() => import('./components/HasPermission')),
  'Components.Icon': moduleLoader(() => import('./components/Navigation')),
  'Components.Iframe': moduleLoader(() => import('./components/Display')),
  'Components.KeyValueList': moduleLoader(() => import('./components/KeyValueList')),
  'Components.Link': moduleLoader(() => import('./components/Navigation')),
  'Components.List': moduleLoader(() => import('./components/DataViews')),
  'Components.LoginForm': moduleLoader(() => import('./components/LoginForm')),
  'Components.LogoutButton': moduleLoader(() => import('./components/UserMenu')),
  'Components.Map': moduleLoader(() => import('./components/Display')),
  'Components.Modal': moduleLoader(() => import('./components/Modal')),
  'Components.NavMain': moduleLoader(() => import('./components/NavMain')),
  'Components.NotificationComposer': moduleLoader(
    () => import('./components/NotificationComposer')
  ),
  'Components.ScheduledNotificationComposer': moduleLoader(
    () => import('./components/ScheduledNotificationComposer')
  ),
  'Components.NotificationConsentForm': moduleLoader(
    () => import('./components/NotificationConsentForm')
  ),
  'Components.NotificationHistory': moduleLoader(() => import('./components/NotificationHistory')),
  'Components.NotificationVariablesForm': moduleLoader(
    () => import('./components/NotificationVariablesForm')
  ),
  'Components.PackageReleaseNotes': moduleLoader(() => import('./components/PackageReleaseNotes')),
  'Components.PageHeader': moduleLoader(() => import('./components/PageHeader')),
  'Components.PaymentForm': moduleLoader(() => import('./components/PaymentForm')),
  'Components.PaymentMethods': moduleLoader(() => import('./components/PaymentMethods')),
  'Components.Popover': moduleLoader(() => import('./components/Popover')),
  'Components.QrCode': moduleLoader(() => import('./components/QrCode')),
  'Components.RecordFields': moduleLoader(() => import('./components/RecordFields')),
  'Components.RecordScheduleDisplay': moduleLoader(
    () => import('./components/RecordScheduleDisplay')
  ),
  'Components.ResourceTable': moduleLoader(() => import('./components/ResourceTable')),
  'Components.RestoreResourceRecord': moduleLoader(
    () => import('./components/RestoreResourceRecord')
  ),
  'Components.RichText': moduleLoader(() => import('./components/Display')),
  'Components.SearchBar': moduleLoader(() => import('./components/ui/SearchBar')),
  'Components.Separator': moduleLoader(() => import('./components/ui/Separator')),
  'Components.SharedFilters': moduleLoader(() => import('./components/SharedFilters')),
  'Components.ShowResourceRecord': moduleLoader(() => import('./components/ShowResourceRecord')),
  'Components.Sidebar': moduleLoader(() => import('./components/ui/Sidebar')),
  'Components.SidebarCollapseButton': moduleLoader(() => import('./components/Sidebar')),
  'Components.SidebarContent': moduleLoader(() => import('./components/ui/Sidebar')),
  'Components.SidebarFooter': moduleLoader(() => import('./components/ui/Sidebar')),
  'Components.SidebarHeader': moduleLoader(() => import('./components/Sidebar')),
  'Components.SidebarInset': moduleLoader(() => import('./components/ui/Sidebar')),
  'Components.SidebarMenu': moduleLoader(() => import('./components/ui/Sidebar')),
  'Components.SidebarMenuButton': moduleLoader(() => import('./components/ui/Sidebar')),
  'Components.SidebarMenuItem': moduleLoader(() => import('./components/ui/Sidebar')),
  'Components.SidebarProvider': moduleLoader(() => import('./components/ui/Sidebar')),
  'Components.SidebarTrigger': moduleLoader(() => import('./components/ui/Sidebar')),
  'Components.SimpleTable': moduleLoader(() => import('./components/SimpleTable')),
  'Components.Skeleton': moduleLoader(() => import('./components/ui/Skeleton')),
  'Components.Slideover': moduleLoader(() => import('./components/Slideover')),
  'Components.SlotPicker': moduleLoader(() => import('./components/SlotPicker')),
  'Components.StatCard': moduleLoader(() => import('./components/StatCard')),
  'Components.SubscriptionBillingPreview': moduleLoader(
    () => import('./components/SubscriptionBillingPreview')
  ),
  'Components.SubscriptionChangePreview': moduleLoader(
    () => import('./components/SubscriptionChangePreview')
  ),
  'Components.SupportChatButton': moduleLoader(() => import('./components/SupportChatButton')),
  'Components.Tabs': moduleLoader(() => import('./components/Tabs')),
  'Components.ThemeToggle': moduleLoader(() => import('./components/ThemeToggle')),
  'Components.Timer': moduleLoader(() => import('./components/Display')),
  'Components.Tooltip': moduleLoader(() => import('./components/Tooltip')),
  'Components.TwoFactorBackupCodeList': moduleLoader(
    () => import('./components/TwoFactorSendControls')
  ),
  'Components.TwoFactorBackupCodes': moduleLoader(
    () => import('./components/TwoFactorBackupCodes')
  ),
  'Components.TwoFactorChallenge': moduleLoader(() => import('./components/TwoFactorChallenge')),
  'Components.TwoFactorSendCodeButton': moduleLoader(
    () => import('./components/TwoFactorSendControls')
  ),
  'Components.TwoFactorSetup': moduleLoader(() => import('./components/TwoFactorSetup')),
  'Components.TwoFactorSmsConsent': moduleLoader(
    () => import('./components/TwoFactorSendControls')
  ),
  'Components.UpdateResourceRecordForm': moduleLoader(
    () => import('./components/UpdateResourceRecordForm')
  ),
  'Components.UserMenu': moduleLoader(() => import('./components/UserMenu')),
  'Components.Video': moduleLoader(() => import('./components/Display')),
  'Components.WorkflowForm': moduleLoader(() => import('./components/WorkflowForm')),
  'Components.WorkspaceFieldsForm': moduleLoader(() => import('./components/WorkspaceFieldsForm')),
  'hooks.useCalendarEvents': moduleLoader(() => import('./components/calendar/useCalendarEvents')),
  'hooks.useCalendarSelection': moduleLoader(
    () => import('./components/calendar/useCalendarSelection')
  ),
  'hooks.useCalendarView': moduleLoader(() => import('./components/calendar/useCalendarView')),
  'hooks.useCreateResourceRecordForm': moduleLoader(
    () => import('./hooks/useCreateResourceRecordForm')
  ),
  'hooks.useDataTable': moduleLoader(() => import('./hooks/useDataTable')),
  'hooks.useNewCalendarEvent': moduleLoader(
    () => import('./components/calendar/useNewCalendarEvent')
  ),
  'hooks.useResourceDataTable': moduleLoader(() => import('./hooks/useResourceDataTable')),
  'hooks.useUpdateResourceRecordForm': moduleLoader(
    () => import('./hooks/useUpdateResourceRecordForm')
  ),
  'hooks.useWorkflowForm': moduleLoader(() => import('./hooks/useWorkflowForm')),
  'hooks.useWorkspaceFieldsForm': moduleLoader(() => import('./hooks/useWorkspaceFieldsForm')),
  Dnd: moduleLoader(() => import('./dnd/index'))
};

export type SdkMemberName = keyof typeof sdkMemberLoaders;

function isSdkMemberName(name: string): name is SdkMemberName {
  return Object.hasOwn(sdkMemberLoaders, name);
}

/**
 * Starts the load of each named member that has not loaded. An unknown name
 * is a member that loads with the page runtime, so it needs nothing. Returns
 * null when every member is already resident.
 */
export function preloadSdkMembers(members: readonly string[]): Promise<unknown> | null {
  const loading = members
    .filter(isSdkMemberName)
    .map((name) => sdkMemberLoaders[name])
    .filter((loader) => loader.loaded() === null)
    .map((loader) => loader.preload());

  return loading.length > 0 ? Promise.all(loading) : null;
}
